import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getToken } from "@/utils/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import Lucide from "@/components/Base/Lucide";
import { Menu, Popover } from "@/components/Base/Headless";
import Pagination from "@/components/Base/Pagination";
import { FormInput, FormSelect } from "@/components/Base/Form";
import Button from "@/components/Base/Button";

type Team = {
    id: string;
    name: string;
    description?: string;
    regionId?: string;
    managerId?: string;
    salesTarget?: number;  
    status: boolean;
    startDate?: string;    
    endDate?: string;      
    teamCatId?: string;    
    companyId?: string;    
    projectId?: string;    
    qrPaymentUrl?: string; 
    enableTeamRoute?: boolean; 
    imageUrl?: string;     
    createdAt?: string;    
    updatedAt?: string;
    memberCount?: number; 
    outletCount?: number;
    productCount?: number;
    // Relations (populated when needed)
    teamCategory?: {
        id: string;
        teamCategory: string;
    };
    region?: {
        id: string;
        name: string;
    };
    manager?: {
        id: string;
        firstName: string;
        lastName: string;
        username: string;
    };
};

function TeamsPage() {
    const [teams, setTeams] = useState<Team[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");

    const navigate = useNavigate();

    // REMOVED: fetchMemberCounts, fetchOutletCounts, fetchProductCounts
    // These functions were making 3 API calls per team (30+ calls for 10 teams!)
    // Counts are now returned directly from the backend in the main teams query

    useEffect(() => {
        (async () => {
            try {
                const companyId = new URLSearchParams(window.location.search).get("companyId");
                const projectId = new URLSearchParams(window.location.search).get("projectId");

                const queryParams = new URLSearchParams();
                if (companyId) queryParams.append("companyId", companyId);
                if (projectId) queryParams.append("projectId", projectId);
                if (searchQuery) queryParams.append("q", searchQuery);
                queryParams.append("page", currentPage.toString());

                const res = await fetch (
                    `${import.meta.env.VITE_API_URL}/teams?${queryParams.toString()}`,
                    {
                        headers: { Authorization: `Bearer ${getToken()}` }
                    }
                );

                const data = await res.json();
                const teamsData = Array.isArray(data?.data) ? data.data : [];
                setTeams(teamsData);
                setTotalPages(data.totalPages || 1);

                // OPTIMIZATION: Removed individual API calls for counts
                // TODO: Backend should return counts in the main teams query
                // This was causing 3 API calls per team (30+ calls for 10 teams!)
                // For now, show 0 or fetch counts on-demand when user clicks

            } catch (err) {
                console.error("Failed to fetch teams:", err);
            } finally {
                setLoading(false);
            }
        })();
    }, [currentPage, searchQuery]);

    const handleDelete = async (id: string) => {
        if (!confirm("Are you sure you want to delete this team?")) return;

        try {
            const res = await fetch(`${import.meta.env.VITE_API_URL}/teams/${id}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${getToken()}`
                }
            });

            if (res.ok) {
                setTeams(prev => prev.filter(p => p.id !== id));
            } else {
                const error = await res.json();
                alert(`Failed to delete team: ${error?.error || "Unknown error"}`);
            }
        } catch (error: unknown) {
            console.error("Delete failed", error);
            alert("Something went wrong deleting team");
        }
    };

    const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setCurrentPage(1);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <Lucide icon="Loader2" className="w-8 h-8 animate-spin mx-auto mb-2" />
                    <div>Loading teams...</div>
                </div>
            </div>
        );
    };

    return (
        <ProtectedRoute>
            <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
                <div className="grid grid-cols-12 gap-y-10 gap-x-6 max-w-7xl mx-auto">
                    <div className="col-span-12">
                            <div className="flex flex-col md:h-10 gap-y-3 md:items-center md:flex-row">
                            <div className="text-base font-medium group-[.mode--light]:text-white">
                                Teams
                            </div>
                            <div className="flex flex-col sm:flex-row gap-x-3 gap-y-2 md:ml-auto">
                                <Button
                                    variant="primary"
                                    className="group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box"
                                    onClick={() => {
                                        const searchParams = new URLSearchParams(window.location.search);
                                        const companyId = searchParams.get("companyId");
                                        const projectId = searchParams.get("projectId");

                                        let addUrl = "/teams/add";
                                        if (projectId) {
                                            addUrl += `?projectId=${projectId}`;
                                        } else if (companyId) {
                                            addUrl += `?companyId=${companyId}`;
                                        }

                                        navigate(addUrl);
                                    }}
                                >
                                    <Lucide icon="PenLine" className="stroke-[1.3] w-4 h-4 mr-2" />{" "}
                                    Add New Team
                                </Button>

                                {/* New buttons for Team Category and Region */}
                                <Button
                                    variant="outline-secondary"
                                    className="group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box"
                                    onClick={() => {
                                        const searchParams = new URLSearchParams(window.location.search);
                                        const companyId = searchParams.get("companyId");
                                        const projectId = searchParams.get("projectId");

                                        let catUrl = "/team-categories";
                                        if (projectId) {
                                            catUrl += `?projectId=${projectId}`;
                                        } else if (companyId) {
                                            catUrl += `?companyId=${companyId}`;
                                        }

                                        navigate(catUrl);
                                    }}
                                >
                                    <Lucide icon="Package" className="stroke-[1.3] w-4 h-4 mr-2" />{" "}
                                    Team Category
                                </Button>

                                <Button
                                    variant="outline-secondary"
                                    className="group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box"
                                    onClick={() => {
                                        const searchParams = new URLSearchParams(window.location.search);
                                        const companyId = searchParams.get("companyId");
                                        const projectId = searchParams.get("projectId");

                                        let regionUrl = "/regions";
                                        if (projectId) {
                                            regionUrl += `?projectId=${projectId}`;
                                        } else if (companyId) {
                                            regionUrl += `?companyId=${companyId}`;
                                        }

                                        navigate(regionUrl);
                                    }}
                                >
                                    <Lucide icon="Tag" className="stroke-[1.3] w-4 h-4 mr-2" />{" "}
                                    Region
                                </Button>
                            </div>
                        </div>
                        <div className="mt-3.5">
                            <div className="flex flex-col box box--stacked max-w-full overflow-hidden">
                                <div className="flex flex-col p-5 sm:items-center sm:flex-row gap-y-2">
                                    <div>
                                        <form onSubmit={handleSearch}>
                                            <div className="relative">
                                                <Lucide
                                                    icon="Search"
                                                    className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500"
                                                />
                                                <FormInput
                                                    type="text"
                                                    placeholder="Search teams..."
                                                    className="pl-9 sm:w-64 rounded-[0.5rem]"
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                />
                                            </div>
                                        </form>
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-x-3 gap-y-2 sm:ml-auto">
                                        <Menu>
                                            <Menu.Button
                                                as={Button}
                                                variant="outline-secondary"
                                                className="w-full sm:w-auto"
                                            >
                                                <Lucide
                                                    icon="Download"
                                                    className="stroke-[1.3] w-4 h-4 mr-2"
                                                />
                                                Export
                                                <Lucide
                                                    icon="ChevronDown"
                                                    className="stroke-[1.3] w-4 h-4 ml-2"
                                                />
                                            </Menu.Button>
                                            <Menu.Items className="w-40">
                                                <Menu.Item>
                                                    <Lucide icon="FileBarChart" className="w-4 h-4 mr-2" />{" "}
                                                    PDF
                                                </Menu.Item>
                                                <Menu.Item>
                                                    <Lucide icon="FileBarChart" className="w-4 h-4 mr-2" />
                                                    CSV
                                                </Menu.Item>
                                            </Menu.Items>
                                        </Menu>
                                        <Popover className="inline-block">
                                            {({ close }) => (
                                                <>
                                                    <Popover.Button
                                                        as={Button}
                                                        variant="outline-secondary"
                                                        className="w-full sm:w-auto"
                                                    >
                                                        <Lucide
                                                            icon="ArrowDownWideNarrow"
                                                            className="stroke-[1.3] w-4 h-4 mr-2"
                                                        />
                                                        Filter
                                                        <div className="flex items-center justify-center h-5 px-1.5 ml-2 text-xs font-medium border rounded-full bg-slate-100 dark:bg-darkmode-400">
                                                            3
                                                        </div>
                                                    </Popover.Button>
                                                    <Popover.Panel placement="bottom-end">
                                                        <div className="p-2">
                                                            <div>
                                                                <div className="text-left text-slate-500">
                                                                    Status
                                                                </div>
                                                                <FormSelect className="flex-1 mt-2">
                                                                    <option value="Active">Active</option>
                                                                    <option value="Inactive">Inactive</option>
                                                                </FormSelect>
                                                            </div>
                                                            <div className="mt-3">
                                                                <div className="text-left text-slate-500">
                                                                    Type
                                                                </div>
                                                                <FormSelect className="flex-1 mt-2">
                                                                    <option value="Regular">Regular</option>
                                                                    <option value="Competitor">Competitor</option>
                                                                </FormSelect>
                                                            </div>
                                                            <div className="flex items-center mt-4">
                                                                <Button
                                                                    variant="secondary"
                                                                    onClick={() => {
                                                                        close();
                                                                    }}
                                                                    className="w-32 ml-auto"
                                                                >
                                                                    Close
                                                                </Button>
                                                                <Button variant="primary" className="w-32 ml-2">
                                                                    Apply
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </Popover.Panel>
                                                </>
                                            )}
                                        </Popover>
                                    </div>
                                </div>

                                <div className="grid grid-cols-12 gap-y-10 gap-x-6 mt-3.5 px-5 max-w-full">
                                    {teams.map((team) => (
                                        <div
                                            className="flex flex-col col-span-12 p-5 md:col-span-6 xl:col-span-4 box box--stacked"
                                            key={team.id}
                                        >
                                            <div className="flex items-center justify-between">
                                                <a
                                                    className="font-medium text-primary text-[0.94rem]"
                                                    href=""
                                                >
                                                    {team.name}
                                                </a>
                                                <div className="text-xs text-slate-500">
                                                    {team.startDate ? new Date(team.startDate).toLocaleDateString() : 'N/A'} - {team.endDate ? new Date(team.endDate).toLocaleDateString() : 'N/A'}
                                                </div>
                                            </div>
                                            <div className="mt-1 mb-5 leading-relaxed text-slate-500">
                                                {team.description}
                                            </div>
                                            <div className="flex flex-col gap-3 pt-5 mt-auto border-t border-dashed border-slate-300/70">
                                                <div className="flex items-center">
                                                    <div className="text-slate-500">Team Category:</div>
                                                    <div className="ml-auto text-slate-500">
                                                        {team.teamCategory?.teamCategory || 'N/A'}
                                                    </div>
                                                </div>
                                                <div className="flex items-center">
                                                    <div className="text-slate-500">Region:</div>
                                                    <div className="ml-auto">
                                                        <div className="flex items-center justify-center">
                                                            <div className="text-slate-500">
                                                                {team.region?.name || 'N/A'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center">
                                                    <div className="text-slate-500">Supervisor/Team Leader:</div>
                                                    <div className="ml-auto">
                                                        <div className="flex items-center justify-center">
                                                            <div className="text-slate-500">
                                                                {team.manager 
                                                                    ? `${team.manager.firstName} ${team.manager.lastName}` 
                                                                    : 'N/A'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center">
                                                    <div className="text-slate-500">Sales Target:</div>
                                                    <div className="ml-auto">
                                                        <a
                                                            href=""
                                                            className="text-slate-500 whitespace-nowrap underline decoration-dotted decoration-slate-500/30 underline-offset-[3px] truncate w-40 md:w-52 block text-right"
                                                        >
                                                            {team.salesTarget ? `$${team.salesTarget.toLocaleString()}` : 'N/A'}
                                                        </a>
                                                    </div>
                                                </div>
                                                <div className="flex items-center">
                                                    <div className="text-slate-500">No. of members:</div>
                                                    <div className="ml-auto">
                                                        <button
                                                            onClick={() => {
                                                                const searchParams = new URLSearchParams(window.location.search);
                                                                const projectId = searchParams.get("projectId");
                                                                const companyId = searchParams.get("companyId");
                                                                
                                                                let assignUserUrl = `/teams/${team.id}/assign-user`;
                                                                if (projectId) {
                                                                    assignUserUrl += `?projectId=${projectId}`;
                                                                } else if (companyId) {
                                                                    assignUserUrl += `?companyId=${companyId}`;
                                                                }
                                                                
                                                                navigate(assignUserUrl);
                                                            }}
                                                            className="flex items-center text-xs rounded-md text-primary bg-primary/10 border border-primary/10 px-1.5 py-px hover:bg-primary/20 hover:border-primary/20 transition-colors cursor-pointer"
                                                        >
                                                            <Lucide icon="Users" className="w-3 h-3 mr-1" />
                                                            <span className="-mt-px">{team.memberCount || 0}</span>
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="flex items-center">
                                                    <div className="text-slate-500">No. of outlets:</div>
                                                    <div className="ml-auto">
                                                        <button
                                                            onClick={() => {
                                                                const searchParams = new URLSearchParams(window.location.search);
                                                                const projectId = searchParams.get("projectId");
                                                                const companyId = searchParams.get("companyId");

                                                                let assignOutletUrl = `/teams/${team.id}/assign-outlet`;
                                                                if (projectId) {
                                                                    assignOutletUrl += `?projectId=${projectId}`;
                                                                } else if (companyId) {
                                                                    assignOutletUrl += `?companyId=${companyId}`;
                                                                }

                                                                navigate(assignOutletUrl);
                                                            }}
                                                            className="flex items-center text-xs rounded-md text-primary bg-primary/10 border border-primary/10 px-1.5 py-px hover:bg-primary/20 hover:border-primary/20 transition-colors cursor-pointer"
                                                        >
                                                            <Lucide icon="Store" className="w-3 h-3 mr-1" />
                                                            <span className="-mt-px">{team.outletCount || 0}</span>
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="flex items-center">
                                                    <div className="text-slate-500">No. of products:</div>
                                                    <div className="ml-auto">
                                                        <button
                                                            onClick={() => {
                                                                const searchParams = new URLSearchParams(window.location.search);
                                                                const projectId = searchParams.get("projectId");
                                                                const companyId = searchParams.get("companyId");

                                                                let assignProductUrl = `/teams/${team.id}/assign-product`;
                                                                if (projectId) {
                                                                    assignProductUrl += `?projectId=${projectId}`;
                                                                } else if (companyId) {
                                                                    assignProductUrl += `?companyId=${companyId}`;
                                                                }

                                                                navigate(assignProductUrl);
                                                            }}
                                                            className="flex items-center text-xs rounded-md text-primary bg-primary/10 border border-primary/10 px-1.5 py-px hover:bg-primary/20 hover:border-primary/20 transition-colors cursor-pointer"
                                                        >
                                                            <Lucide icon="Package" className="w-3 h-3 mr-1" />
                                                            <span className="-mt-px">{team.productCount || 0}</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Bottom Action Buttons */}
                                            <div className="flex items-center justify-end pt-4">
                                                <Link
                                                    to={(() => {
                                                        const searchParams = new URLSearchParams(window.location.search);
                                                        const projectId = searchParams.get("projectId");
                                                        const companyId = searchParams.get("companyId");
                                                        
                                                        let editUrl = `/teams/${team.id}/edit`;
                                                        if (projectId) {
                                                            editUrl += `?projectId=${projectId}`;
                                                        } else if (companyId) {
                                                            editUrl += `?companyId=${companyId}`;
                                                        }
                                                        
                                                        return editUrl;
                                                    })()}
                                                    className="flex items-center mr-3"
                                                >
                                                    <Lucide
                                                        icon="CheckSquare"
                                                        className="w-4 h-4 stroke-[1.3] mr-1.5"
                                                    />{" "}
                                                    Edit
                                                </Link>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDelete(team.id)}
                                                    className="flex items-center text-danger"
                                                    aria-label={`Delete ${team.name}`}
                                                >
                                                    <Lucide
                                                        icon="Trash2"
                                                        className="w-4 h-4 stroke-[1.3] mr-1.5"
                                                    />{" "}
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                
                                <div className="flex flex-col-reverse flex-wrap items-center p-5 flex-reverse gap-y-2 sm:flex-row">
                                    <Pagination className="flex-1 w-full mr-auto sm:w-auto">
                                        <Pagination.Link>
                                            <Lucide icon="ChevronsLeft" className="w-4 h-4" />
                                        </Pagination.Link>
                                        <Pagination.Link>
                                            <Lucide icon="ChevronLeft" className="w-4 h-4" />
                                        </Pagination.Link>
                                        <Pagination.Link>...</Pagination.Link>
                                        {Array.from({ length: totalPages }).map((_, i) => (
                                            <Pagination.Link
                                                key={i}
                                                active={currentPage === i + 1}
                                                onClick={() => setCurrentPage(i + 1)}
                                            >
                                                {i + 1}
                                            </Pagination.Link>
                                        ))}
                                        <Pagination.Link>
                                            <Lucide icon="ChevronRight" className="w-4 h-4" />
                                        </Pagination.Link>
                                        <Pagination.Link>
                                            <Lucide icon="ChevronsRight" className="w-4 h-4" />
                                        </Pagination.Link>
                                    </Pagination>
                                    <FormSelect className="sm:w-20 rounded-[0.5rem]">
                                        <option>10</option>
                                        <option>25</option>
                                        <option>35</option>
                                        <option>50</option>
                                    </FormSelect>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </RoleGate>
        </ProtectedRoute>
    );
}

export default TeamsPage;